import React, { useState } from "react";
import {
	Button,
	Dialog,
	DialogContent,
	DialogTitle,
	DialogActions,
	Typography,
	Box,
	CircularProgress,
	Alert,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
} from "@mui/material";
import { Pin as PinIcon, CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import rootStyles from "../styles.module.scss";
import { NotistackServices, UserServices } from "shared/services";
import { OTPInput } from "components";
import { useAccount } from "shared/hooks";

const PIN_TIMEOUT_OPTIONS = [
	{ value: 5, label: "5 minutos" },
	{ value: 10, label: "10 minutos" },
	{ value: 15, label: "15 minutos" },
	{ value: 30, label: "30 minutos" },
	{ value: 60, label: "1 hora" },
	{ value: 120, label: "2 horas" },
	{ value: 240, label: "4 horas" },
	{ value: 480, label: "8 horas" },
];

interface RenderEnableContentProps {
	onClose?: () => void;
}

const RenderEnableContent: React.FC<RenderEnableContentProps> = ({ onClose }) => {
	const [loading, setLoading] = useState(false);
	const [step, setStep] = useState(0);
	const [pin, setPin] = useState<string[]>(Array(4).fill(""));
	const [confirmPin, setConfirmPin] = useState<string[]>(Array(4).fill(""));
	const [error, setError] = useState<string | null>(null);

	const handleClose = () => {
		if (loading) return;
		onClose?.();
	};

	const handleConfirmEnable = async () => {
		const pinCode = pin.join("");
		const confirmPinCode = confirmPin.join("");

		if (pinCode !== confirmPinCode) {
			setError("Os PINs não coincidem. Tente novamente.");
			return;
		}

		setLoading(true);
		setError(null);

		try {
			await UserServices.enablePin(pinCode);
			setStep(2);
		} catch (err: any) {
			setError(err.message || "Erro ao ativar o PIN.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<DialogTitle>Ativar PIN de Segurança</DialogTitle>

			<DialogContent
				dividers
				sx={{ paddingY: 3 }}
			>
				{step === 0 && (
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
						<Typography
							variant="body1"
							textAlign="center"
						>
							Crie um PIN de 4 dígitos que será solicitado após períodos de inatividade na tela.
						</Typography>
						<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
							<Typography
								variant="body2"
								color="textSecondary"
							>
								Digite seu PIN:
							</Typography>
							<OTPInput
								value={pin}
								onChange={setPin}
							/>
						</Box>
						{error && <Alert severity="error">{error}</Alert>}
					</Box>
				)}

				{step === 1 && (
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
						<Typography
							variant="body1"
							textAlign="center"
						>
							Confirme o PIN digitado anteriormente.
						</Typography>
						<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
							<Typography
								variant="body2"
								color="textSecondary"
							>
								Confirme seu PIN:
							</Typography>
							<OTPInput
								value={confirmPin}
								onChange={setConfirmPin}
							/>
						</Box>
						{error && <Alert severity="error">{error}</Alert>}
					</Box>
				)}

				{step === 2 && (
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 3 }}>
						<CheckCircleIcon
							color="success"
							sx={{ fontSize: 64 }}
						/>
						<Typography variant="h5">PIN Ativado com Sucesso!</Typography>
						<Typography
							variant="body1"
							textAlign="center"
						>
							Seu PIN de segurança está ativo. Ele será solicitado após períodos de inatividade na tela.
						</Typography>
					</Box>
				)}
			</DialogContent>

			<DialogActions>
				{step === 0 && (
					<>
						<Button
							color="inherit"
							onClick={handleClose}
						>
							Cancelar
						</Button>
						<Button
							variant="contained"
							onClick={() => {
								if (pin.join("").length !== 4) {
									setError("Digite um PIN de 4 dígitos.");
									return;
								}
								setError(null);
								setStep(1);
							}}
							disabled={pin.join("").length !== 4}
						>
							Próximo
						</Button>
					</>
				)}

				{step === 1 && (
					<>
						<Button
							color="inherit"
							onClick={() => {
								setConfirmPin(Array(4).fill(""));
								setError(null);
								setStep(0);
							}}
						>
							Voltar
						</Button>
						<Button
							variant="contained"
							onClick={handleConfirmEnable}
							disabled={confirmPin.join("").length !== 4 || loading}
							loading={loading}
						>
							Ativar PIN
						</Button>
					</>
				)}

				{step === 2 && (
					<Button
						variant="outlined"
						onClick={handleClose}
					>
						Fechar
					</Button>
				)}
			</DialogActions>
		</>
	);
};

interface RenderDisableContentProps {
	onClose?: () => void;
}

const RenderDisableContent: React.FC<RenderDisableContentProps> = ({ onClose }) => {
	const [loading, setLoading] = useState(false);
	const [step, setStep] = useState(0);
	const [pin, setPin] = useState<string[]>(Array(4).fill(""));
	const [error, setError] = useState<string | null>(null);

	const handleClose = () => {
		if (loading) return;
		onClose?.();
	};

	const handleVerifyDisable = async () => {
		setLoading(true);
		setError(null);
		try {
			const code = pin.join("");
			if (code.length !== 4) {
				throw new Error("Digite o PIN de 4 dígitos.");
			}
			await UserServices.disablePin(code);
			setStep(1);
		} catch (err: any) {
			setError(err.message || "PIN incorreto ou erro ao desativar.");
		}
		setLoading(false);
	};

	return (
		<>
			<DialogContent
				dividers
				sx={{ paddingY: 3 }}
			>
				{step === 0 && (
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
						<Alert
							severity="warning"
							sx={{ width: "100%" }}
						>
							Atenção: Desativar o PIN de segurança removerá a proteção contra acesso não autorizado durante inatividade.
						</Alert>
						<Typography
							variant="body1"
							textAlign="center"
						>
							Para confirmar, digite seu PIN de 4 dígitos.
						</Typography>
						<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
							<OTPInput
								value={pin}
								onChange={setPin}
							/>
						</Box>
						{error && <Alert severity="error">{error}</Alert>}
					</Box>
				)}

				{step === 1 && (
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 3 }}>
						<CheckCircleIcon
							color="success"
							sx={{ fontSize: 64 }}
						/>
						<Typography variant="h5">PIN Desativado</Typography>
						<Typography variant="body1">O PIN de segurança foi removido da sua conta.</Typography>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				{step === 0 && (
					<>
						<Button
							onClick={handleClose}
							disabled={loading}
						>
							Cancelar
						</Button>
						<Button
							variant="contained"
							color="error"
							onClick={handleVerifyDisable}
							disabled={pin.join("").length !== 4 || loading}
							loading={loading}
						>
							Desativar PIN
						</Button>
					</>
				)}
				{step === 1 && (
					<Button
						variant="outlined"
						onClick={handleClose}
					>
						Fechar
					</Button>
				)}
			</DialogActions>
		</>
	);
};

export const PinProcess = () => {
	const user = useAccount();
	const [open, setOpen] = useState(false);
	const [mode, setMode] = useState<"enable" | "disable">("enable");
	const [timeoutMinutes, setTimeoutMinutes] = useState<number>(user?.pinTimeoutMinutes ?? 30);
	const [updatingTimeout, setUpdatingTimeout] = useState(false);

	const handleOpenEnable = () => {
		setMode("enable");
		setOpen(true);
	};

	const handleOpenDisable = () => {
		setMode("disable");
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleTimeoutChange = async (value: number) => {
		setUpdatingTimeout(true);
		try {
			await UserServices.updatePinTimeout(value);
			setTimeoutMinutes(value);
			NotistackServices.showSuccessMessage("Tempo de inatividade atualizado!");
		} catch (err: any) {
			NotistackServices.showErrorMessage(err.message || "Erro ao atualizar tempo de inatividade.");
		} finally {
			setUpdatingTimeout(false);
		}
	};

	return (
		<>
			<div className={rootStyles.card}>
				<div className={rootStyles.btmRow}>
					<div className={rootStyles.info}>
						<div className={rootStyles.rowIcon}>
							<PinIcon />
						</div>
						<div className={rootStyles.text}>
							<h5>PIN de Segurança</h5>
							<p>Proteja sua conta com um PIN de 4 dígitos solicitado após inatividade.</p>
						</div>
					</div>
					<Button
						variant="contained"
						color={user?.pinEnabled ? "error" : "primary"}
						onClick={user?.pinEnabled ? handleOpenDisable : handleOpenEnable}
					>
						{user?.pinEnabled ? "Desativar" : "Ativar PIN"}
					</Button>
				</div>

				{user?.pinEnabled && (
					<Box sx={{ mt: 2, pt: 2, borderTop: "1px solid rgba(var(--mui-palette-dividerChannel) / 0.12)" }}>
						<FormControl
							size="small"
							sx={{ minWidth: 200 }}
						>
							<InputLabel id="pin-timeout-label">Tempo de inatividade</InputLabel>
							<Select
								labelId="pin-timeout-label"
								value={timeoutMinutes}
								label="Tempo de inatividade"
								onChange={(e) => handleTimeoutChange(e.target.value as number)}
								disabled={updatingTimeout}
							>
								{PIN_TIMEOUT_OPTIONS.map((opt) => (
									<MenuItem
										key={opt.value}
										value={opt.value}
									>
										{opt.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>
				)}
			</div>

			<Dialog
				open={open}
				onClose={(event, reason) => {
					if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
						handleClose();
					}
				}}
				maxWidth="sm"
				fullWidth
				scroll="paper"
			>
				{mode === "enable" ? <RenderEnableContent onClose={handleClose} /> : <RenderDisableContent onClose={handleClose} />}
			</Dialog>
		</>
	);
};
